import { Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  buildImageUrl,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import http from "../../../commonFunctions/httpClient";
import config from "../../../Config/config.json";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import WSButton from "../../../Components/Button/WSButton";
import WSSelectField from "../../../Components/Select/WSSelect";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSImageUpload from "../../../Components/FileUpload/WSImageUpload";
import WSBackButton from "../../../Components/BackButton/BackButton";

interface ScanSubCategoryFormData {
  Name: string;
  Description: string;
  AlsoKnownAs: string;
  PreparationInstructions: string;
  TurnaroundTime: string;
  ScancategoryId: string;
  imageFile: File | string | null;
}

const ScanSubCategoryCreate: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const id: any = params?.id;
  const isEditMode = !!id;

  const [loading, setLoading] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<ScanSubCategoryFormData>({ mode: "onChange" });

  useEffect(() => {
    updateMainHeader(isEditMode ? "Update Scan Sub Category" : "Add Scan Sub Category");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Sub Category": "/scan-sub-category/list" },
      { [isEditMode ? "Update" : "Add"]: "/" },
    ]);
  }, [isEditMode]);

  // Load parent Scan Categories for the dropdown
  useEffect(() => {
    const fetchScanCategories = async () => {
      try {
        const response: any = await http.post("pharmacy/ScanCategory/search", {});
        const list =
          response?.data || response?.items || response?.categories || response || [];
        const options = (Array.isArray(list) ? list : []).map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setCategoryOptions(options);
      } catch (error) {
        console.error("Error loading scan categories:", error);
        setCategoryOptions([]);
      }
    };
    fetchScanCategories();
  }, []);

  // Load existing record for edit
  useEffect(() => {
    const getById = async () => {
      if (!id) return;
      try {
        const response: any = await http.get(`pharmacy/ScanSubCategory/${id}`);
        const data = response?.data || response;
        if (data) {
          setValue("Name", data.name || "");
          setValue("Description", data.description || "");
          setValue("AlsoKnownAs", data.alsoKnownAs || "");
          setValue("PreparationInstructions", data.preparationInstructions || "");
          setValue("TurnaroundTime", data.turnaroundTime || "");
          setValue("ScancategoryId", data.scancategoryId || data.scanCategoryId || "");
          if (data.imgUrl) {
            setExistingImage(buildImageUrl(data.imgUrl, config.imgUrl));
          }
        }
      } catch (error) {
        console.error("Error fetching scan sub category:", error);
        showMessage("Error fetching scan sub category", "error");
      }
    };
    getById();
  }, [id, setValue, showMessage]);

  const onSubmit = async (data: ScanSubCategoryFormData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("Name", data.Name || "");
      formData.append("Description", data.Description || "");
      formData.append("AlsoKnownAs", data.AlsoKnownAs || "");
      formData.append("PreparationInstructions", data.PreparationInstructions || "");
      formData.append("TurnaroundTime", data.TurnaroundTime || "");
      formData.append("ScancategoryId", data.ScancategoryId || "");
      // Only append the image when the user actually selected a new file
      if (data.imageFile instanceof File) {
        formData.append("imageFile", data.imageFile);
      }

      let response: any;
      if (isEditMode && id) {
        response = await http.putFormData(`pharmacy/ScanSubCategory/${id}`, formData);
      } else {
        response = await http.postFormData("pharmacy/ScanSubCategory", formData);
      }

      const success =
        response?.success === true ||
        !!response?.id ||
        (!!response && !response?.errors && !response?.title);

      if (success) {
        showMessage(
          response?.message ||
            (isEditMode
              ? "Scan sub category updated successfully!"
              : "Scan sub category created successfully!"),
          "success"
        );
        navigate("/scan-sub-category/list");
      } else {
        const errMsg =
          response?.message ||
          (response?.errors ? JSON.stringify(response.errors) : null) ||
          "Failed to save scan sub category";
        showMessage(errMsg, "error");
      }
    } catch (e: any) {
      console.error("Submit error:", e);
      showMessage(
        e?.response?.data?.message || e?.message || "Failed to save scan sub category",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/scan-sub-category/list");

  return (
    <>
      <WSBackButton id="btnScanSubBack" url="/scan-sub-category/list" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              style={{
                paddingLeft: 20,
                paddingRight: 20,
                paddingBottom: 25,
                borderRadius: "8px",
              }}
            >
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                    Scan Sub Category Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <WSImageUpload
                    control={control}
                    id="imageFile"
                    name="imageFile"
                    label="Image"
                    rules={{ required: false }}
                    imgUrl={existingImage}
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="Name"
                    name="Name"
                    rules={{ required: true }}
                    label="Name *"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSSelectField
                    rules={{ required: true }}
                    control={control}
                    id="ScancategoryId"
                    name="ScancategoryId"
                    label="Scan Category *"
                    options={categoryOptions}
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="AlsoKnownAs"
                    name="AlsoKnownAs"
                    rules={{ required: false }}
                    label="Also Known As"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="TurnaroundTime"
                    name="TurnaroundTime"
                    rules={{ required: false }}
                    label="Turnaround Time"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="Description"
                    name="Description"
                    rules={{ required: false }}
                    label="Description"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="PreparationInstructions"
                    name="PreparationInstructions"
                    rules={{ required: false }}
                    label="Preparation Instructions"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
            <Grid item>
              <WSButton
                id="cancel"
                type="button"
                label="Cancel"
                size="medium"
                onClick={handleCancel}
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  border: "1px solid #098963",
                  color: "#098963",
                  backgroundColor: "#FFFFFF",
                }}
              />
            </Grid>
            <Grid item>
              <WSButton
                id="submit"
                type="submit"
                label={isEditMode ? "Update" : "Save"}
                size="medium"
                variant="outlined"
                color="primary"
                loading={loading}
                disabled={!isValid || loading}
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  color: "#FFFFFF",
                  backgroundColor: "#098963",
                  "&:hover": { backgroundColor: "#066e4f" },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
});

export default ScanSubCategoryCreate;
